// Per-browser "last viewed profile" tracking, used to show a "new update"
// badge on the Manage Customers list when the customer's medical form or
// guest preferences were touched after the operator last opened the profile.
//
// Kept in localStorage (not synced across devices / staff members) because
// this is UX hint plumbing — the source of truth still lives on the customer
// document. A stale local record just means we might miss showing the badge,
// which is a soft failure the operator can spot manually.

const KEY = "cocoon.customer.last_viewed";

// Read the full map from localStorage. Guard the parse so a bad shape doesn't
// break the whole customers page — we just start fresh.
function readMap() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const p = JSON.parse(raw);
    return (p && typeof p === "object") ? p : {};
  } catch { return {}; }
}

function writeMap(map) {
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch { /* quota exceeded — silent */ }
}

// Stamp NOW as the last-viewed timestamp for `cid`. Call this every time the
// operator opens a customer profile so subsequent list renders can compare
// against it.
export function markCustomerViewed(cid) {
  if (!cid) return;
  const map = readMap();
  map[cid] = new Date().toISOString();
  writeMap(map);
}

// Return the "most recent activity" ISO on `customer` from the fields we
// track for badges. Currently: medical_updated_at + guest_preferences[*]
// updated_at/created_at. Returns null when there's nothing to compare.
function mostRecentActivity(customer) {
  const stamps = [];
  if (customer?.medical_updated_at) stamps.push(customer.medical_updated_at);
  const prefs = Array.isArray(customer?.guest_preferences) ? customer.guest_preferences : [];
  for (const p of prefs) {
    if (p?.updated_at) stamps.push(p.updated_at);
    else if (p?.created_at) stamps.push(p.created_at);
  }
  if (!stamps.length) return null;
  return stamps.sort()[stamps.length - 1];
}

// Whether the row should get a "new update" badge. Truthy return also
// identifies which stream is fresh so the badge can label it (e.g.
// "medical" vs "preferences" — future extension).
export function customerHasUpdates(customer) {
  const activity = mostRecentActivity(customer);
  if (!activity) return false;
  const lastSeen = readMap()[customer.id];
  // Never viewed → treat as "new" only when the activity was performed by the
  // customer (auto-fill from the portal). Staff-side activity is already known
  // to the person adding it, so we don't want to flag every customer at first
  // open. `medical_updated_by === "customer"` marks customer-side saves;
  // guest_preferences don't carry an updater tag so we err on the side of
  // showing.
  if (!lastSeen) {
    return customer.medical_updated_by === "customer" ||
      (Array.isArray(customer.guest_preferences) && customer.guest_preferences.length > 0);
  }
  return activity > lastSeen;
}

// Debug/utility — export a "clear" hook in case we ever need to wipe the map
// (e.g. sign-out).
export function clearCustomerViewLog() {
  writeMap({});
}
